'use client'
import { Box, Typography, useTheme } from '@mui/material'
import Grid from '@mui/material/Grid';
import * as Colors from '@mui/material/colors'

const PRIMARY_PALLETE = [
    "primary",
    "secondary"
]

const EMPHASIS_PALLETE = [
    "error",
    "warning",
    "info",
    "success"
]

const MATERIAL_UI_PALLETE = [
  "red",
  "pink",
  "purple",
  "deepPurple",
  "indigo",
  "blue",
  "lightBlue",
  "cyan",
  "teal",
  "green",
  "lightGreen",
  "lime",
  "yellow",
  "amber",
  "orange",
  "deepOrange",
  "brown",
  "grey",
  "blueGrey",
  "chart-by",
  "divergent"
]

export default function PalleteColorsShowcase() {
    const { palette } = useTheme();
    const { getContrastText } = palette;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const customPallete = palette as any;

        
    return (
        <Box display={"flex"} flexDirection="column" gap={4}>
            <Grid container spacing={2}>
                {PRIMARY_PALLETE.map((color) => (
                    <Grid
                        key={color}
                        sx={{ maxWidth: { md: '14rem' } }}
                        size={{
                            xs: 12,
                            sm: 6
                        }}>
                        <Box key={`pallete-${color}`} sx={{
                            borderRadius: 2,
                            border: 1,
                            borderColor: 'grey.400',
                            borderStyle: 'solid',
                            overflow: 'hidden',
                        }}>
                            <Typography sx={{ fontWeight: 'bold', my: 0, py: 0, px: 1 }}>{color}</Typography>
                            <Box
                                bgcolor={customPallete[color].main}
                                sx={{ width: '100%', height: '14rem', pt: 0, mt: 0 }}
                            >
                                <Typography color={getContrastText(customPallete[color].main)} sx={{ fontSize: 'xs', fontWeight: 'bold', p: 0.5 }}>main</Typography>
                            </Box>
                            <Box key="primary" display="flex" alignItems="center" sx={{ pt: 0, mt: 0 }}>
                                <Box bgcolor={customPallete[color].light} sx={{ width: '50%', height: '7rem', pt: 0, mt: 0 }}>
                                    <Typography color={getContrastText(customPallete[color].main)} sx={{ fontSize: 'xs', fontWeight: 'bold', p: 0.5 }}>light</Typography>
                                </Box>
                                <Box bgcolor={customPallete[color].dark} sx={{ width: '50%', height: '7rem', pt: 0, mt: 0 }}>
                                    <Typography color={getContrastText(customPallete[color].main)} sx={{ fontSize: 'xs', fontWeight: 'bold', p: 0.5 }}>dark</Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                ))}
            </Grid>
            <Grid container spacing={2}>
                {EMPHASIS_PALLETE.map((color) => (
                    <Grid
                        key={color}
                        sx={{ maxWidth: { md: '6rem' } }}
                        size={{
                            xs: 6,
                            sm: 3
                        }}>
                        <Box key={color} sx={{
                            borderRadius: '0.375rem',
                            border: '1px solid',
                            borderColor: 'grey.400',
                            overflow: 'hidden'
                        }}>
                            <Typography sx={{ fontWeight: 'bold', my: 0, py: 0, px: 1 }}>{color}</Typography>
                            <Box
                                bgcolor={customPallete[color].main}
                                color={getContrastText(customPallete[color].main)}
                                sx={{ width: '100%', height: '6rem', pt: 0, mt: 0 }}
                            >
                                <Typography variant="body2" color={getContrastText(customPallete[color].main)} sx={{ fontSize: 'xs', fontWeight: 'bold', p: 0.5 }}>main</Typography>
                            </Box>
                            <Box key="primary" display="flex" alignItems="center" sx={{ pt: 0, mt: 0 }}>
                                <Box bgcolor={customPallete[color].light} color={getContrastText(customPallete[color].light)} sx={{ width: '50%', height: '3rem', pt: 0, mt: 0 }}>
                                    <Typography variant="body2" sx={{ fontSize: 'xs', fontWeight: 'bold', p: 0.5 }}>light</Typography>
                                </Box>
                                <Box bgcolor={customPallete[color].dark} color={getContrastText(customPallete[color].dark)} sx={{ width: '50%', height: '3rem', pt: 0, mt: 0 }}>
                                    <Typography variant="body2" sx={{ fontSize: 'xs', fontWeight: 'bold', p: 0.5 }}>dark</Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                ))}
            </Grid>
            <Grid container spacing={2}>
                {MATERIAL_UI_PALLETE.map((color) => (
                    <Grid
                        key={`${color}-${(Colors as never)[color]}`}
                        size={{
                            xs: 4,
                            sm: 2,
                            md: 1
                        }}>
                        <Box key={`${color}-${(Colors as never)[color]}`} sx={{
                            borderRadius: '0.375rem',
                            border: '1px solid',
                            borderColor: 'grey.400',
                            overflow: 'hidden'
                        }}>
                            <Typography sx={{ fontWeight: 'bold', my: 0, py: 0, px: 1 }}>{color}</Typography>
                            <Box key={color} display="flex" flexDirection="column" alignItems="start" sx={{ pt: 0, mt: 0 }}>
                                {Object.keys((Colors as never)[color]).map((shade) => (
                                    <Box
                                        key={`${color}-${shade}`}
                                        bgcolor={(Colors as never)[color][shade]}
                                        color={getContrastText((Colors as never)[color][shade])}
                                        sx={{ 
                                            width: '100%', 
                                            height: '3.5rem', 
                                            pt: 0, 
                                            mt: shade === 'A100' ? 0.25 : 0, 
                                            display: 'flex', 
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexDirection: 'column',
                                            px: 2 
                                        }}
                                    >
                                        <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }} color={getContrastText((Colors as never)[color][shade])}>{shade}</Typography>
                                        <Typography variant="caption">{(Colors as never)[color][shade]}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Grid>
                ))}
            </Grid>
            <Typography sx={{ fontWeight: 'bold' }}>* Material UI colors are not part of the theme palette</Typography>
        </Box>
    );
}
